import { ENUM_UTILS, abbreviateAddress } from '@velocity-exchange/common';
import { PublicKey } from '@solana/web3.js';
import { openUrl } from 'src/utils/browser';
import { Account, Lightning, Velocity } from '@velocity-exchange/icons';
import { MarketType, OrderActionExplanation } from '@velocity-exchange/sdk';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { cj } from 'src/utils/classNames';

interface CounterPartyProps {
	counterparty: string;
	marketType: MarketType;
	actionExplanation: OrderActionExplanation;
	isGenericTrade?: boolean;
	isTaker?: boolean;
	isSignedMsgTrade?: boolean;
}

const Counterparty = ({
	counterparty,
	marketType,
	actionExplanation,
	isGenericTrade,
	isTaker,
	isSignedMsgTrade,
}: CounterPartyProps) => {
	const isAmmTrade =
		!counterparty ||
		ENUM_UTILS.match(
			actionExplanation,
			OrderActionExplanation.ORDER_FILLED_WITH_AMM
		);

	const isJitTrade = ENUM_UTILS.match(
		actionExplanation,
		OrderActionExplanation.ORDER_FILLED_WITH_MATCH_JIT
	);

	const isPerp = ENUM_UTILS.match(marketType, MarketType.PERP);
	const ammCounterparty = isPerp ? 'Velocity AMM' : 'Velocity';

	const tooltipText = (() => {
		if (isJitTrade) {
			if (isTaker || isGenericTrade) {
				return `Trade filled by [${getCounterpartyAddress(
					counterparty
				)}] with just-in-time liquidity`;
			}

			return `[${getCounterpartyAddress(
				counterparty
			)}] was filled by just-in-time liquidity`;
		}

		if (isAmmTrade) {
			return `Trade filled by ${ammCounterparty}`;
		}

		return `${
			isGenericTrade ? 'Trade filled by' : 'Counterparty'
		} [${getCounterpartyAddress(counterparty)}]`;
	})();

	const tooltipTextPlusSwift = isSignedMsgTrade
		? `${tooltipText} (via Swift)`
		: tooltipText;

	return (
		<Tooltip content={tooltipTextPlusSwift}>
			<div
				className={cj(
					'flex items-center transition-colors',
					!isAmmTrade && 'cursor-pointer hover:text-text-default',
					isSignedMsgTrade ? 'text-royal-purple-200' : 'text-text-secondary'
				)}
				onClick={() => {
					if (!isAmmTrade) {
						openUrl(`/portfolio/trade?userAccount=${counterparty}`);
					}
				}}
			>
				<CounterpartyIcon
					counterparty={counterparty}
					marketType={marketType}
					actionExplanation={actionExplanation}
				/>
			</div>
		</Tooltip>
	);
};

type CounterpartyIconProps = Pick<
	CounterPartyProps,
	'counterparty' | 'marketType' | 'actionExplanation'
>;

const CounterpartyIcon = ({
	counterparty,
	marketType,
	actionExplanation,
}: CounterpartyIconProps) => {
	const isAmmTrade =
		!counterparty ||
		ENUM_UTILS.match(
			actionExplanation,
			OrderActionExplanation.ORDER_FILLED_WITH_AMM
		);
	const isJitTrade = ENUM_UTILS.match(
		actionExplanation,
		OrderActionExplanation.ORDER_FILLED_WITH_MATCH_JIT
	);
	const isPerp = ENUM_UTILS.match(marketType, MarketType.PERP);

	if (isJitTrade) {
		return <Lightning />;
	}

	if (!isAmmTrade) {
		return <Account />;
	}

	if (isPerp) {
		return <Velocity />;
	}

	return null;
};

const getCounterpartyAddress = (counterparty: string) => {
	return abbreviateAddress(new PublicKey(counterparty));
};

export default Counterparty;
